import { BarcodeType } from '~/modules/devices/types/barcode.type';
import { fetchOrderV2 } from '~/modules/orders/services/ordersV2.service';
import { type OrderShipmentV2 } from '~/modules/orders/types/ordersV2.type';
import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type CreateScanBarcodeContentOptions,
  type ScanBarcodeContent,
} from '../types/scanContext.type';
import { normalizeObjectId } from './normalization.util';

/**
 * Resolve an order shipment to the most recent with matching rank. Higher batch
 * entry IDs are expected to represent newer IDs, matching Haven logic.
 */
const resolveOrderShipment = (
  orderShipments: OrderShipmentV2[],
  scannedOrderShipment: OrderShipmentV2,
) =>
  orderShipments
    .filter(
      ({ is_return, split_order_rank }) =>
        is_return === scannedOrderShipment.is_return &&
        split_order_rank === scannedOrderShipment.split_order_rank,
    )
    .reduce(
      (latest, orderShipment) => (orderShipment.id > latest.id ? orderShipment : latest),
      scannedOrderShipment,
    );

export const createScanBarcodeContent = async ({
  barcodeValue,
  correlationId,
  fetchOrder = fetchOrderV2,
  resolveBarcode,
}: CreateScanBarcodeContentOptions): Promise<ScanBarcodeContent | undefined> => {
  try {
    const barcode = await resolveBarcode(barcodeValue);
    if (barcode?.barcode_type !== BarcodeType.OrderShipment) return undefined;

    const orderObjectId = normalizeObjectId(barcode.barcode_content.order_id);
    const orderShipmentObjectId = normalizeObjectId(barcode.barcode_content.shipment_id);

    const { order_shipments: orderShipments } = await fetchOrder(orderObjectId, {
      include_all_shipments: true,
    });
    const scannedOrderShipment = orderShipments.find(
      ({ object_id }) => object_id === orderShipmentObjectId,
    );

    if (!scannedOrderShipment) {
      newRelic.log('Failed to find Order Shipment', {
        customAttributes: {
          barcodeValue,
          correlationId,
          orderObjectId,
          orderShipmentObjectId,
        },
      });
      return undefined;
    }

    const orderShipment = resolveOrderShipment(orderShipments, scannedOrderShipment);

    if (orderShipment.object_id !== scannedOrderShipment.object_id) {
      newRelic.log('Scan resolved a replacement Order Shipment', {
        customAttributes: {
          barcodeValue,
          correlationId,
          orderObjectId,
          orderShipmentObjectId,
          resolvedOrderShipmentObjectId: orderShipment.object_id,
          splitOrderRank: orderShipment.split_order_rank,
        },
        level: 'INFO',
      });
    }

    return {
      order_object_id: orderObjectId,
      order_shipment_object_id: orderShipment.object_id,
      shipment_id: String(orderShipment.id),
      ...(orderShipment.transaction_object_id && {
        transaction_object_id: orderShipment.transaction_object_id,
      }),
    };
  } catch (error) {
    newRelic.noticeError('Failed to resolve scan barcode content', { correlationId, error });

    return undefined;
  }
};
